import a11yIIBScreenReaderService from '../../../../../services/a11yIIBScreenReaderService';
import { t } from '../../../../../services/i18nService';

const isA11yIterationForIIBScreenReaderEnabled = () => {
    return a11yIIBScreenReaderService.isA11yIterationForIIBScreenReaderSupported();
};

const extendedTextCodeA11ySelector = {
    /**
     * Gets accessibility attributes for the code entry question group container
     * @returns {Object} Object containing aria attributes for the code entry question group
     */
    getCodeQuestionGroupA11yAttributes: () => {
        const role = isA11yIterationForIIBScreenReaderEnabled() ? 'group' : undefined;
        return { role };
    },

    /**
     * Gets accessibility attributes for the test cases table
     * @param {object} model - The interaction model
     * @returns {object} Object containing aria attributes for the test cases table
     */
    getTestCasesTableA11yAttributes: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'role': undefined,
                'aria-label': undefined,
                'aria-describedby': undefined,
            };
        }

        return {
            'role': 'table',
            'aria-label': t('extendedTextCode.testCasesTableAriaLabel'),
            'aria-describedby': t('extendedTextCode.keyboardInstructions'),
        };
    },

    /**
     * Gets accessibility attributes for the code editor wrapper
     * @param {object} model - The interaction model
     * @returns {object} Object containing aria attributes for the code editor
     */
    getCodeEditorA11yAttributes: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'role': undefined,
                'aria-label': undefined,
                'aria-multiline': undefined,
                'aria-describedby': undefined,
            };
        }

        return {
            'role': 'textbox',
            'aria-label': t('extendedTextCode.codeEditorAriaLabel'),
            'aria-multiline': 'true',
            'aria-describedby': t('extendedTextCode.helpInstructions'),
        };
    },

    /**
     * Gets accessibility attributes for the test output region
     * @returns {object} Object containing aria attributes for the test output region
     */
    getTestOutputRegionA11yAttributes: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'role': undefined,
                'aria-label': undefined,
                'aria-live': undefined,
                'aria-atomic': undefined,
            };
        }

        return {
            'role': 'region',
            'aria-label': t('extendedTextCode.testOutputAriaLabel'),
            'aria-live': 'polite',
            'aria-atomic': 'true',
        };
    },

    /**
     * Gets accessibility attributes for the run test button
     * @param {string} outputRegionId - The ID of the output region for aria-controls
     * @returns {object} Object containing aria attributes for the run test button
     */
    getRunTestButtonA11yAttributes: (outputRegionId) => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'aria-controls': undefined,
                'aria-label': undefined,
                'aria-describedby': undefined,
                'tabIndex': undefined,
            };
        }

        return {
            'aria-controls': outputRegionId,
            'aria-label': t('extendedTextCode.runTestButtonAriaLabel'),
            'aria-describedby': t('extendedTextCode.helpInstructions'),
            'tabIndex': 0,
        };
    },

    /**
     * Gets keyboard navigation instructions for the code interaction
     * @returns {string|undefined} Keyboard instruction text or undefined
     */
    getKeyboardInstructions: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return undefined;
        }

        return t('extendedTextCode.keyboardInstructions');
    },

    /**
     * Gets accessibility attributes for the help button
     * @returns {object} Object containing aria attributes for the help button
     */
    getHelpButtonA11yAttributes: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                'aria-label': t('interactions.keyboardHelp'),
            };
        }

        return {
            'aria-label': t('extendedTextCode.helpButtonAriaLabel'),
        };
    },

    /**
     * Gets keyboard navigation instructions for the interaction
     * @returns {object} Object containing keyboard navigation details
     */
    getKeyboardNavigationInstructions: () => {
        if (!isA11yIterationForIIBScreenReaderEnabled()) {
            return {
                tabKeyAction: undefined,
                escKeyAction: undefined,
            };
        }

        return {
            tabKeyAction: t('extendedTextCode.tabKeyAction'),
            escKeyAction: t('extendedTextCode.escKeyAction'),
        };
    },
};

export default extendedTextCodeA11ySelector;
